"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FiEdit2, FiSave } from "react-icons/fi";
import AlertDialog from "./AlertDialog";

export default function CompanyProfileManagement() {
  const [isAddingProfile, setIsAddingProfile] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);

  const [profileData, setProfileData] = useState({
    companyName: "",
    phoneNumber: "",
    address: { street: "", city: "", state: "", zip: "", country: "" },
    active: true,
  });

  const [logoFile, setLogoFile] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");
      const res = await axios
        .get("https://rentdoc-a0d223e1499e.herokuapp.com/api/company-profile", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch((err) => {
          if (err.response?.status === 404) return { data: null };
          throw err;
        });

      setCompanyProfile(res.data);
      if (res.data) {
        setProfileData({
          companyName: res.data.companyName || "",
          phoneNumber: res.data.phoneNumber || "",
          address: res.data.address || {
            street: "",
            city: "",
            state: "",
            zip: "",
            country: "",
          },
          active: res.data.active ?? true,
          plugins: res.data.plugins,
        });
      }
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.status === 404
            ? "Company profile not found. Please create a profile."
            : `Failed to load company profile: ${err.message}`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setProfileData((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value },
      }));
    } else {
      setProfileData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleToggle = () =>
    setProfileData((prev) => ({ ...prev, active: !prev.active }));

  const handleAddOrUpdateProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("companyName", profileData.companyName);
      formData.append("phoneNumber", profileData.phoneNumber);
      formData.append("address[street]", profileData.address.street);
      formData.append("address[city]", profileData.address.city);
      formData.append("address[state]", profileData.address.state);
      formData.append("address[zip]", profileData.address.zip);
      formData.append("address[country]", profileData.address.country);
      formData.append("active", profileData.active);
      if (logoFile) formData.append("logo", logoFile);

      const url =
        "https://rentdoc-a0d223e1499e.herokuapp.com/api/company-profile";
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      };
      const res = isEditingProfile
        ? await axios.put(url, formData, { headers })
        : await axios.post(url, formData, { headers });

      setCompanyProfile(res.data);
      setIsAddingProfile(false);
      setIsEditingProfile(false);
      resetProfileData();
      setLogoFile(null);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: `Company profile ${isEditingProfile ? "updated" : "added"} successfully!`,
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to save company profile. Please try again.",
        onConfirm: () => setDialog((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleEditClick = () => {
    if (companyProfile) {
      setProfileData({
        companyName: companyProfile.companyName || "",
        phoneNumber: companyProfile.phoneNumber || "",
        address: companyProfile.address || {
          street: "",
          city: "",
          state: "",
          zip: "",
          country: "",
        },
        active: companyProfile.active ?? true,
        plugins: companyProfile.plugins || { quickbooks: { lastSynced: null } },
      });
      setIsEditingProfile(true);
    }
  };

  const resetProfileData = () => {
    setProfileData({
      companyName: "",
      phoneNumber: "",
      address: { street: "", city: "", state: "", zip: "", country: "" },
      active: true,
    });
  };

  const closeModal = () => {
    setIsAddingProfile(false);
    setIsEditingProfile(false);
    resetProfileData();
    setLogoFile(null);
  };

  const modalOpen = isAddingProfile || isEditingProfile;

  const inputClass =
    "w-full bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3 px-4 text-sm text-gray-900";
  const labelClass =
    "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="mt-8">
      <AlertDialog
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        confirmText="OK"
        cancelText="Close"
        open={dialog.open}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={dialog.onConfirm}
      />

      {/* Section header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Company Profile
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Your organization's details and branding
          </p>
        </div>
        {!companyProfile ? (
          <button
            onClick={() => setIsAddingProfile(true)}
            className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl shadow-lg transition"
          >
            + Add Company Profile
          </button>
        ) : (
          <button
            onClick={handleEditClick}
            className="flex items-center gap-2 px-6 py-2.5 bg-white border border-gray-200 hover:border-indigo-500 hover:text-indigo-600 text-gray-700 text-sm font-semibold rounded-2xl transition shadow-sm"
          >
            <FiEdit2 className="w-4 h-4" />
            Edit Profile
          </button>
        )}
      </div>

      {/* Profile card */}
      {companyProfile ? (
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
          <div className="flex items-start gap-8">
            {/* Logo */}
            {companyProfile.logo && (
              <div className="flex-shrink-0">
                <img
                  src={companyProfile.logo}
                  alt="Company Logo"
                  className="w-24 h-24 object-contain rounded-2xl border border-gray-100 bg-gray-50 p-2"
                />
              </div>
            )}

            {/* Info grid */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Company Name
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {companyProfile.companyName}
                </p>
              </div>

              {companyProfile.phoneNumber && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Phone
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {companyProfile.phoneNumber}
                  </p>
                </div>
              )}

              {companyProfile.address && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Address
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {companyProfile.address.street}
                    <br />
                    {companyProfile.address.city},{" "}
                    {companyProfile.address.state} {companyProfile.address.zip}
                    <br />
                    {companyProfile.address.country}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Status
                </p>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    companyProfile.active
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${companyProfile.active ? "bg-green-500" : "bg-red-400"}`}
                  />
                  {companyProfile.active ? "Active" : "Inactive"}
                </span>
              </div>

              {companyProfile.numberOfUsers != null && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Total Users
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {companyProfile.numberOfUsers}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-12 text-center">
          <div className="text-gray-400 text-xl mb-1">
            No company profile yet
          </div>
          <p className="text-sm text-gray-500">Add a profile to get started</p>
        </div>
      )}

      {/* Modal */}
      <Dialog open={modalOpen} onClose={closeModal} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-3xl bg-white shadow-2xl">
              {/* Modal header */}
              <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-gray-100">
                <div>
                  <DialogTitle className="text-xl font-semibold text-gray-900">
                    {isEditingProfile
                      ? "Edit Company Profile"
                      : "Add Company Profile"}
                  </DialogTitle>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Update your organization's details
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Modal body */}
              <div className="px-8 py-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Company Name</label>
                    <input
                      type="text"
                      name="companyName"
                      value={profileData.companyName}
                      onChange={handleProfileChange}
                      placeholder="Acme Inc."
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Phone Number</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={profileData.phoneNumber}
                      onChange={handleProfileChange}
                      placeholder="+1 (555) 123-4567"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Street Address</label>
                  <input
                    type="text"
                    name="address.street"
                    value={profileData.address.street}
                    onChange={handleProfileChange}
                    placeholder="123 Main St"
                    className={inputClass}
                  />
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>City</label>
                    <input
                      type="text"
                      name="address.city"
                      value={profileData.address.city}
                      onChange={handleProfileChange}
                      placeholder="Toronto"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>State / Province</label>
                    <input
                      type="text"
                      name="address.state"
                      value={profileData.address.state}
                      onChange={handleProfileChange}
                      placeholder="ON"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Zip / Postal Code</label>
                    <input
                      type="text"
                      name="address.zip"
                      value={profileData.address.zip}
                      onChange={handleProfileChange}
                      placeholder="M5V 3A8"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Country</label>
                    <input
                      type="text"
                      name="address.country"
                      value={profileData.address.country}
                      onChange={handleProfileChange}
                      placeholder="Canada"
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* Logo upload */}
                <div>
                  <label className={labelClass}>Company Logo</label>
                  <div className="mt-1">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={(e) => setLogoFile(e.target.files[0])}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-5 file:rounded-2xl file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 transition"
                    />
                    {logoFile && (
                      <p className="mt-2 text-xs text-gray-500">
                        Selected: {logoFile.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between py-4 border-t border-gray-100">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Active
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Enable or disable this company profile
                    </p>
                  </div>
                  <button
                    onClick={handleToggle}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${profileData.active ? "bg-indigo-600" : "bg-gray-200"}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300 ${profileData.active ? "translate-x-5" : "translate-x-0"}`}
                    />
                  </button>
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex justify-end gap-4 bg-gray-50 px-8 py-5 border-t border-gray-100 rounded-b-3xl">
                <button
                  onClick={closeModal}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddOrUpdateProfile}
                  className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl shadow-lg transition"
                >
                  <FiSave className="w-4 h-4" />
                  {isEditingProfile ? "Update Profile" : "Add Profile"}
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
